import { OrbitControls } from '@react-three/drei'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { useEffect, useMemo, useState } from 'react'
import { ProvedorMaquete, type ValorMaquete } from './componentes/contextoMaquete'
import { CenaTabletop, type PropsCenaTabletop } from './tabletop/CenaTabletop'
import { PalcoEstudio, CONFIGURACAO_GL_ESTUDIO } from './ambiente/PalcoEstudio'
import { ContadorFps } from './ambiente/ContadorFps'
import { MonitorQualidade } from './ambiente/MonitorQualidade'
import { ProvedorPosProcessamento } from './ambiente/registroPosProcessamento'
import { PosProcessamento } from './ambiente/PosProcessamento'
import { useQualidadeAdaptativa } from './ambiente/useQualidadeAdaptativa'
import { RECURSOS_QUALIDADE } from './ambiente/qualidadeGrafica'
import { DestaqueSelecaoAnatomia } from './anatomia/DestaqueSelecaoAnatomia'
import { ControleCamera3D } from './camera/ControleCamera3D'
import { ExplorarCena } from './camera/ExplorarCena'
import { AJUSTES_CAMERA, useNivelDaCamera } from './camera/sensibilidadeCamera'
import {
  DISTANCIA_MINIMA,
  alvoCamera,
  type ComandoCamera,
} from './camera/comandosCamera'

/** A câmera pode ir a qualquer lado, menos para baixo do chão. */
const ALTURA_MINIMA_CAMERA_M = 0.1

/**
 * Sem trava de ângulo, dá para olhar de baixo para cima (o alto da escada, uma ponte) e
 * rodar por cima. Só o chão limita: a câmera para a poucos centímetros dele.
 */
function LimiteDoChao() {
  const camera = useThree((estado) => estado.camera)
  useFrame(() => {
    if (camera.position.y < ALTURA_MINIMA_CAMERA_M) camera.position.y = ALTURA_MINIMA_CAMERA_M
  })
  return null
}

interface Props {
  contexto: ValorMaquete
  tabletop: PropsCenaTabletop
  /** Comando de câmera vindo da trilha de ferramentas. */
  comandoCamera: ComandoCamera
  /** Grade-régua do piso (padrão ligada). */
  comGrade?: boolean
}

/** A cena 3D do módulo atual: o table top, num palco amplo com câmera orbital. */
export function Visualizador3D({ contexto, tabletop, comandoCamera, comGrade = true }: Props) {
  const { perfil } = contexto
  const qualidade = useQualidadeAdaptativa()
  // Referência estável: `<OrbitControls target={...}>` reaplica esta prop a
  // cada render (R3F não faz diff por valor em vetores), e um array novo a
  // cada chamada reescreveria `controls.target` sobre qualquer enquadramento
  // que `ControleCamera3D` tivesse acabado de aplicar.
  const alvoPadraoCamera = useMemo(() => alvoCamera(perfil), [perfil])
  const [tabletopManipulando, setTabletopManipulando] = useState(false)
  // a sensibilidade escolhida no menu da câmera
  const ajuste = AJUSTES_CAMERA[useNivelDaCamera()]

  useEffect(() => {
    if (tabletop.idSelecionado === null) setTabletopManipulando(false)
  }, [tabletop.idSelecionado])

  return (
    <div className="visualizador-3d">
      <Canvas
        camera={{ position: [4.9, 3.4, 5.3], fov: 42 }}
        dpr={RECURSOS_QUALIDADE[qualidade.nivel].dpr}
        shadows="percentage"
        gl={CONFIGURACAO_GL_ESTUDIO}
        // clicar no vazio limpa a seleção
        onPointerMissed={() => tabletop.aoSelecionar(null)}
      >
        <MonitorQualidade
          adaptativa={qualidade.adaptativa}
          aoDecair={qualidade.aoDecair}
          aoSubir={qualidade.aoSubir}
        />
        <ContadorFps qualidade={qualidade.nivel} />
        <PalcoEstudio perfil={perfil} qualidade={qualidade.nivel} ampla comGrade={comGrade} />

        <ProvedorPosProcessamento>
          <ProvedorMaquete valor={contexto}>
            <CenaTabletop
              {...tabletop}
              aoAlterarManipulacao={(ativa) => {
                setTabletopManipulando(ativa)
                tabletop.aoAlterarManipulacao?.(ativa)
              }}
              aoCancelarManipulacao={() => {
                setTabletopManipulando(false)
                tabletop.aoCancelarManipulacao?.()
              }}
            />
            <DestaqueSelecaoAnatomia />
          </ProvedorMaquete>
          <PosProcessamento qualidade={qualidade.nivel} oclusaoAmbiente />
        </ProvedorPosProcessamento>

        <OrbitControls
          makeDefault
          enabled={
            !(tabletopManipulando || tabletop.escolhendoAlvo) &&
            !(tabletop.pincel?.ativo && tabletop.objetos.some(
              (objeto) => objeto.id === tabletop.idSelecionado && Boolean(objeto.veiculo),
            ))
          }
          target={alvoPadraoCamera}
          minDistance={DISTANCIA_MINIMA}
          maxDistance={90}
          maxPolarAngle={Math.PI}
          screenSpacePanning
          rotateSpeed={ajuste.girar}
          panSpeed={ajuste.arrastar}
          enableDamping
          dampingFactor={ajuste.amortecimento}
          enableZoom
          zoomSpeed={ajuste.aproximar}
          zoomToCursor
        />
        <LimiteDoChao />
        <ExplorarCena velocidadeAndar={ajuste.andarMPorS} />
        <ControleCamera3D comando={comandoCamera} perfil={perfil} />
      </Canvas>
    </div>
  )
}

